// ============================================================
// EL CHAT DEL ENTRENADOR, EN EL PANEL
// ============================================================
// Medido por Jose en el APK y medido contra producción con la sesión del
// entrenador: esta pantalla —y su gemela de la app— pintaba un DIRECTORIO, no
// conversaciones. 41 tarjetas de gente a la que podría escribir, 2 hilos de
// verdad, y el mensaje sin leer que el globo contaba era de un TUTOR, que no
// tiene tarjeta en ninguna parte. Ver runbook §118.
//
// Aquí era peor que en la app, y por un motivo aparte: esta página no usaba la
// vista `profiles_chat_contacts` sino `profiles` con `role='alumno'`. Medido
// con su sesión: **34 alumnos y 0 staff**. O sea que el BUG 17 original —el
// admin le escribe y no hay tarjeta donde abrirlo— seguía vivo en el panel
// aunque la mig 285 lo hubiera cerrado en la vista.
//
// LO QUE HACE AHORA, Y ES DECISIÓN DE JOSE
// ----------------------------------------
//   · Al entrar se ven los HILOS ABIERTOS, cada uno con sus no leídos, y
//     ordenados por el último mensaje. El «no leídos primero» de antes existía
//     para subir a los que habían escrito en una lista de 41; con hilos ese
//     motivo desaparece.
//   · El directorio se va detrás de la lupa y del «+», que abren lo mismo.
//   · El hilo es de la CASA, como la bandeja del club desde la mig 277: «un
//     entrenador no necesita saber si le escribe el padre o la madre».
//   · Y al contestar se contesta AL TUTOR QUE ESCRIBIÓ: el último de la casa
//     que habló, con selector «Para» cuando hay más de un miembro. Misma regla
//     que `ComunicacionesPage`.
//
// LAS TRES PUERTAS DE LA BASE
// ---------------------------
//   `messages` ....................... los hilos y sus no leídos salen de aquí
//                                      y de ningún otro sitio.
//   `interlocutores_del_chat()` ...... mig 308. Quién le ha escrito, con
//                                      nombre y con la clave de su casa. Es la
//                                      única forma de saber que existe un
//                                      tutor: un entrenador no puede leer ni
//                                      `profiles` de tutor ni `guardians`
//                                      (medido: 0 filas).
//   `profiles_chat_contacts` ......... el directorio, que ahora sí trae al
//                                      staff. Con `family_keys` encima para
//                                      que un hermano que nunca escribió caiga
//                                      en la casa correcta en vez de abrir un
//                                      hilo duplicado.
//
// ⚠️ Y SIN EMBED DE `profiles`. La consulta del hilo llevaba
// `sender:profiles!messages_sender_id_fkey(full_name)`, y para el mensaje de un
// tutor eso devuelve `null`: la RLS de `profiles` no le da ninguna vía a un
// entrenador hacia un tutor (medido, 0 filas). El nombre sale del mapa de
// personas, igual que en `MonitorChatScreen` desde la 7.C2.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Send, Search, AlertCircle, Plus, Users } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { supabase } from '../../services/supabase';
import { useAuthStore } from '../../store/authStore';
import { leerVarias, listaEnCastellano } from '../../lib/leerVarias';
import { initials, nombreDeFamilia, inicialesDeFamilia } from '../../lib/familias';
import { marcarLeidos } from '../../lib/lecturas';
import type { RealtimeChannel } from '@supabase/supabase-js';

/** Una persona del otro lado, con lo justo para rotularla y escribirle. */
interface Persona {
  id: string;
  full_name: string;
  role: string | null;
}

/**
 * Un hilo. La clave es la de la CASA (`family_key`, mig 277) cuando la hay, y
 * el id de la persona cuando no: el staff no es familia, y quien no tenga
 * clave cae en su propia conversación en vez de amontonarse con otro.
 */
interface Hilo {
  clave: string;
  nombre: string;
  /** El otro lado entero: a esto se le marca leído y de esto sale el «Para». */
  miembros: Persona[];
  ultimo: string;
  ultimoAt: string;
  noLeidos: number;
}

interface Mensaje {
  id: string;
  sender_id: string;
  receiver_id: string | null;
  content: string;
  created_at: string;
  is_mine: boolean;
}

type MensajeCrudo = {
  id: string;
  sender_id: string;
  receiver_id: string | null;
  content: string;
  created_at: string;
  read: boolean;
};
type ContactoCrudo = { id: string; full_name: string; role: string };
type InterlocutorCrudo = {
  persona_id: string;
  full_name: string;
  role: string;
  clave_familiar: string | null;
};
type ClaveCruda = { id: string; family_key: string | null };

// Con hilo abierto, `destinatarioId` solo se queda a null si el hilo llegó SIN
// miembros, y eso no puede pasar: los miembros se siembran con los ids que
// salen de los propios mensajes del hilo. Se deja dicho porque es la tercera
// condición del botón apagado, y un botón apagado por un motivo que no se
// enseña es un callejón sin salida.
const MOTIVO_SIN_DESTINATARIO =
  'No hay a quién escribir: este hilo llegó sin miembros. Recargue la lista de conversaciones.';

/** Un hueco, no un nombre. Ver #469: un «Usuario» inventado se lee como dato. */
const SIN_NOMBRE = '(sin nombre)';

function msgTime(iso: string) {
  const d = new Date(iso);
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Ayer';
  return format(d, 'dd/MM');
}

/**
 * La firma de una burbuja: de quién es este mensaje dentro de la casa.
 *
 * Solo cuando la casa tiene más de un miembro, que es cuando hace falta: con
 * uno ya lo dice la cabecera y repetirlo en cada burbuja es ruido. Mira el
 * lado — si el mensaje es mío, la persona que importa es a QUIÉN se lo mandé;
 * si es de la casa, QUIÉN lo escribió.
 *
 * Sin fallback, a propósito. Si el nombre no está, la burbuja se queda sin
 * firma: un destinatario inventado en un hilo con un padre y dos hermanos
 * decide a quién crees que estás contestando.
 */
function firmaDeLaBurbuja(m: Mensaje, miembros: Persona[]): string {
  if (miembros.length < 2) return '';
  const quienId = m.is_mine ? m.receiver_id : m.sender_id;
  if (!quienId) return '';
  return miembros.find((x) => x.id === quienId)?.full_name?.trim() ?? '';
}

export default function MonitorComunicacionesPage() {
  const { profile } = useAuthStore();
  const [hilos, setHilos] = useState<Hilo[]>([]);
  const [contactos, setContactos] = useState<Persona[]>([]);
  const [abierto, setAbierto] = useState<Hilo | null>(null);
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [mostrarDirectorio, setMostrarDirectorio] = useState(false);
  const [sendError, setSendError] = useState('');
  // «Sin conversaciones» es una AFIRMACIÓN: dice que se ha mirado y no hay
  // nada. Con el error descartado, un 403 de RLS decía exactamente lo mismo,
  // con la misma cara. Es cómo el BUG 13 estuvo dos semanas en producción.
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  // Lo que se leyó a medias: los hilos están —salen de `messages`— pero los
  // nombres, el directorio o la agrupación por familias, no. La lista vale; lo
  // que no vale es cómo está rotulada.
  const [avisoParcial, setAvisoParcial] = useState<string | null>(null);
  // «Inicia la conversación» sobre un hilo que no se ha podido leer: el
  // entrenador escribe encima de lo que la familia ya le contó.
  const [errorHilo, setErrorHilo] = useState<string | null>(null);
  // A cuál de la casa se le contesta. `messages` sigue siendo persona a
  // persona (la 277 no toca ni una fila), así que una respuesta va a alguien
  // concreto aunque el hilo sea de la casa.
  const [destinatarioId, setDestinatarioId] = useState<string | null>(null);
  const elegidoAManoRef = useRef(false);
  const endRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const abiertoRef = useRef<Hilo | null>(null);

  useEffect(() => {
    abiertoRef.current = abierto;
  }, [abierto]);

  const cargar = useCallback(async () => {
    if (!profile) return;

    // 1) Los hilos salen de los MENSAJES. Si esto falla no hay lista, y se
    //    dice: lo que no puede pasar es que un fallo se lea como un dato.
    const { data: msgData, error: errorMensajes } = await supabase
      .from('messages')
      .select('id, sender_id, receiver_id, content, created_at, read')
      .or(`sender_id.eq.${profile.id},receiver_id.eq.${profile.id}`)
      .order('created_at', { ascending: false });

    if (errorMensajes) {
      setErrorCarga(errorMensajes.message || 'Error desconocido');
      setLoading(false);
      return;
    }
    setErrorCarga(null);
    // Los broadcast (`receiver_id IS NULL`) fuera: aquí solo hay
    // conversaciones. Hoy no existe ninguno en la tabla —medido, 84 de 84 son
    // uno a uno— pero un broadcast en esta lista sería un hilo inventado.
    const crudos = ((msgData ?? []) as MensajeCrudo[]).filter((m) => m.receiver_id !== null);

    // 2) Quién es quién. Las dos lecturas son independientes y ninguna es
    //    imprescindible para que la lista exista, así que se piden juntas y se
    //    cuenta cuál faltó.
    const { datos, fallidos } = await leerVarias([
      { que: 'los nombres', consulta: supabase.rpc('interlocutores_del_chat') },
      {
        que: 'la lista de contactos',
        consulta: supabase
          .from('profiles_chat_contacts')
          .select('id, full_name, role')
          .order('full_name'),
      },
    ]);
    const interlocutores = (datos[0] as InterlocutorCrudo[] | null) ?? [];
    const delDirectorio = (datos[1] as ContactoCrudo[] | null) ?? [];

    const personas = new Map<string, Persona>();
    const clavePorId = new Map<string, string>();
    // `interlocutores_del_chat()` primero: es la única que sabe de tutores y la
    // única que trae la clave HEREDADA de sus hijos (la propia sería un
    // singleton — mig 308).
    for (const i of interlocutores) {
      personas.set(i.persona_id, { id: i.persona_id, full_name: i.full_name, role: i.role });
      if (i.clave_familiar) clavePorId.set(i.persona_id, i.clave_familiar);
    }
    for (const c of delDirectorio) {
      if (!personas.has(c.id))
        personas.set(c.id, { id: c.id, full_name: c.full_name, role: c.role });
    }
    setContactos(
      [...delDirectorio].map((c) => ({ id: c.id, full_name: c.full_name, role: c.role }))
    );

    // 3) Y las claves del DIRECTORIO, para el hermano que nunca escribió.
    //    Sin esto, elegirlo en «nueva conversación» abriría un hilo aparte al
    //    lado del de su casa: dos conversaciones donde la familia ve una, que
    //    es justo el incidente que cerró la mig 277.
    //
    //    Solo los alumnos pasan por `family_keys`: el staff no es familia, y
    //    se decide por ROL y no confiando en que su clave salga suelta —si un
    //    monitor joven tuviera `parent_email`, el escalón 2 de `family_key` lo
    //    metería en la casa de un alumno sin que nadie lo notara.
    const idsDeAlumnos = delDirectorio.filter((c) => c.role === 'alumno').map((c) => c.id);
    if (idsDeAlumnos.length > 0) {
      const { data: clavesData, error: errorClaves } = await supabase.rpc('family_keys', {
        p_people: idsDeAlumnos,
      });
      if (errorClaves) fallidos.push('la agrupación por familias');
      for (const fila of (clavesData as ClaveCruda[] | null) ?? []) {
        if (fila.family_key && !clavePorId.has(fila.id)) clavePorId.set(fila.id, fila.family_key);
      }
    }

    setAvisoParcial(
      fallidos.length > 0
        ? `No hemos podido cargar ${listaEnCastellano(fallidos)}. Las conversaciones están, pero puede que veas una sin nombre o una casa partida en dos.`
        : null
    );

    // 4) Agrupar. Una clave por persona conocida; quien no tenga, su id.
    const claveDe = (id: string) => clavePorId.get(id) ?? id;

    const miembrosPorClave = new Map<string, Map<string, Persona>>();
    const mete = (p: Persona) => {
      const clave = claveDe(p.id);
      const casa = miembrosPorClave.get(clave) ?? new Map<string, Persona>();
      // El que llega con nombre gana: los sembrados desde los mensajes vienen
      // vacíos y solo están para que el hilo tenga a quién contestar.
      const previo = casa.get(p.id);
      if (!previo || (!previo.full_name && p.full_name)) casa.set(p.id, p);
      miembrosPorClave.set(clave, casa);
    };
    for (const p of personas.values()) mete(p);

    const porClave = new Map<string, { ultimo: string; ultimoAt: string; noLeidos: number }>();
    for (const m of crudos) {
      const otro = m.sender_id === profile.id ? m.receiver_id : m.sender_id;
      if (!otro) continue;
      // Sembrado: aunque no se sepa su nombre, el hilo tiene a quién
      // contestar. Es lo que hace que `destinatarioId` no pueda quedarse a
      // null con los nombres caídos.
      if (!personas.has(otro)) mete({ id: otro, full_name: '', role: null });
      const clave = claveDe(otro);
      // Vienen ordenados por fecha descendente, así que el primero de cada
      // clave es el último mensaje de esa casa.
      if (!porClave.has(clave)) {
        porClave.set(clave, { ultimo: m.content, ultimoAt: m.created_at, noLeidos: 0 });
      }
      // `m.receiver_id === profile.id && !m.read` sigue siendo exacto tras la mig
      // 324: el destinatario es quien pregunta, y el trigger de la 324 deriva ese
      // booleano justo cuando quien registra la lectura es el destinatario. Donde
      // NO vale es en el buzón compartido, y ahí se pregunta por `message_reads`.
      if (m.receiver_id === profile.id && !m.read) porClave.get(clave)!.noLeidos += 1;
    }

    const lista: Hilo[] = [...porClave.entries()]
      .map(([clave, d]) => {
        // Los alumnos primero y por nombre, y detrás el resto. Es el orden en
        // el que se lee una casa —«Adidas, Nike y su padre»— y es el que
        // decide a quién ofrece el «Para» cuando nadie ha escrito todavía.
        const miembros = [...(miembrosPorClave.get(clave)?.values() ?? [])].sort((a, b) => {
          const esAlumno = (x: Persona) => (x.role === 'alumno' ? 0 : 1);
          return esAlumno(a) - esAlumno(b) || a.full_name.localeCompare(b.full_name, 'es');
        });
        return {
          clave,
          nombre: nombreDeFamilia(miembros) || SIN_NOMBRE,
          miembros,
          ...d,
        };
      })
      // Por el último mensaje, lo más reciente arriba.
      .sort((a, b) => b.ultimoAt.localeCompare(a.ultimoAt));

    setHilos(lista);
    // El hilo abierto se refresca con la lista: si no, sus `miembros` se
    // quedarían con la foto de la carga anterior y el «Para» podría ofrecer a
    // alguien que ya no está.
    const previo = abiertoRef.current;
    if (previo) setAbierto(lista.find((h) => h.clave === previo.clave) ?? previo);
    setLoading(false);
  }, [profile]);

  useEffect(() => {
    if (!profile?.id) return;
    cargar();
    const channel = supabase
      .channel('monitor-msgs-web')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => {
        cargar();
        if (abiertoRef.current) loadMensajes(abiertoRef.current);
      })
      .subscribe();
    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
    };
    // El handler accede al hilo abierto vía `abiertoRef.current` (no por dep)
    // para no re-suscribir el canal cada vez que el entrenador abre otra
    // conversación. `loadMensajes` se redefine cada render pero solo lee
    // `profile` por closure, que es estable mientras `profile?.id` no cambie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, cargar]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes]);

  // A quién contesta el entrenador: al último de la casa que escribió. Con un
  // solo miembro es él; con un padre y dos hermanos es el que preguntó, que es
  // lo que uno espera al pulsar responder.
  //
  // ⚠️ Hace falta el ref y no vale con «si ya hay uno, respétalo». El efecto
  // corre DOS veces: primero al abrir el hilo, cuando los mensajes todavía no
  // han llegado y el único candidato es el primer miembro, y otra vez al
  // llegar. Respetando el valor previo se quedaba clavado el primero de la
  // lista, que es el hermano equivocado y sin nada que lo delate. Nota
  // heredada de `ComunicacionesPage`, donde pasó.
  useEffect(() => {
    if (!abierto) {
      setDestinatarioId(null);
      elegidoAManoRef.current = false;
      return;
    }
    if (elegidoAManoRef.current) return;
    const ids = new Set(abierto.miembros.map((m) => m.id));
    const ultimo = [...mensajes].reverse().find((m) => ids.has(m.sender_id));
    setDestinatarioId(ultimo?.sender_id ?? abierto.miembros[0]?.id ?? null);
  }, [abierto, mensajes]);

  async function loadMensajes(hilo: Hilo) {
    if (!profile) return;
    const otroLado = `(${hilo.miembros.map((m) => m.id).join(',')})`;
    const { data, error: errorHilos } = await supabase
      .from('messages')
      .select('id, sender_id, receiver_id, content, created_at')
      .or(
        `and(sender_id.eq.${profile.id},receiver_id.in.${otroLado}),and(sender_id.in.${otroLado},receiver_id.eq.${profile.id})`
      )
      .order('created_at');

    if (errorHilos) {
      // Se vacía a propósito: `mensajes` puede llevar los de la casa ANTERIOR
      // y dejarlos puestos pondría la conversación de una bajo el nombre de
      // otra. El aviso ocupa el hueco.
      setMensajes([]);
      setErrorHilo(errorHilos.message || 'Error desconocido');
      return;
    }
    setErrorHilo(null);
    const rows = (data ?? []) as Omit<MensajeCrudo, 'read'>[];
    setMensajes(
      rows.map((m) => ({
        id: m.id,
        sender_id: m.sender_id,
        receiver_id: m.receiver_id,
        content: m.content,
        created_at: m.created_at,
        is_mine: m.sender_id === profile.id,
      }))
    );
  }

  async function abrirHilo(hilo: Hilo) {
    // El motivo del último envío fallido muere con la conversación: pegado en
    // el hilo de otra casa diría algo falso de ella.
    setSendError('');
    setAbierto(hilo);
    setMostrarDirectorio(false);
    await loadMensajes(hilo);
    if (!profile) return;
    // Contra TODA la casa, no contra un id. Filtrando por uno solo, el mensaje
    // del tutor se quedaría sin leer para siempre dentro del hilo y el globo
    // no se apagaría nunca — el fallo que el chat del alumno ya arregló.
    //
    // Desde la mig 324 una lectura es UNA FILA CON NOMBRE, así que hay que saber
    // los ids: antes bastaba un `update` con filtro. Aquí quien lee ES el
    // destinatario, así que el trigger de la 324 deriva `messages.read` y el
    // globo —que sigue preguntando a `get_unread_message_count`— baja igual.
    const { data: pendientes, error: errorIds } = await supabase
      .from('messages')
      .select('id')
      .in(
        'sender_id',
        hilo.miembros.map((m) => m.id)
      )
      .eq('receiver_id', profile.id);
    const error = errorIds
      ? errorIds.message || 'Error desconocido'
      : (
          await marcarLeidos(
            ((pendientes ?? []) as { id: string }[]).map((m) => m.id),
            profile.id
          )
        ).error;
    // Si la base NO lo ha guardado, el globo no se apaga: apagarlo en local le
    // dice al entrenador que ya está leído, y en la siguiente recarga vuelve a
    // encenderse sin que nadie entienda por qué.
    if (error) return;
    setHilos((prev) => prev.map((h) => (h.clave === hilo.clave ? { ...h, noLeidos: 0 } : h)));
  }

  /**
   * Abrir conversación con alguien del directorio.
   *
   * Si su casa ya tiene hilo, se abre ESE. Empezar uno nuevo con un hermano
   * dejaría dos conversaciones donde la familia ve una.
   */
  function empezarCon(contacto: Persona) {
    const existente = hilos.find((h) => h.miembros.some((m) => m.id === contacto.id));
    if (existente) {
      abrirHilo(existente);
      return;
    }
    setSendError('');
    setMostrarDirectorio(false);
    setAbierto({
      clave: contacto.id,
      nombre: contacto.full_name || SIN_NOMBRE,
      miembros: [contacto],
      ultimo: '',
      ultimoAt: new Date().toISOString(),
      noLeidos: 0,
    });
    setMensajes([]);
    setErrorHilo(null);
  }

  const motivoEnvioDeshabilitado = sending
    ? 'Enviando…'
    : !profile
      ? 'No hay sesión cargada. Recargue la página.'
      : !destinatarioId
        ? MOTIVO_SIN_DESTINATARIO
        : !text.trim()
          ? 'Escriba un mensaje.'
          : null;

  async function sendMessage() {
    if (!text.trim() || !abierto || !profile || sending) return;
    if (!destinatarioId) {
      setSendError(MOTIVO_SIN_DESTINATARIO);
      return;
    }
    setSending(true);
    setSendError('');
    const body = text.trim();
    // Vaciamos optimista pero conservamos el body por si hay que restaurar.
    setText('');

    const destino = destinatarioId;
    const { data: inserted, error: insErr } = await supabase
      .from('messages')
      .insert({ sender_id: profile.id, receiver_id: destino, content: body })
      .select('id, created_at')
      .single();

    if (insErr) {
      // Restaurar el textarea + mostrar el motivo. Sin esto el entrenador ve
      // cómo el mensaje "desaparece" y nada llega a la familia.
      setText(body);
      setSendError(insErr.message ?? 'No se pudo enviar el mensaje.');
      setSending(false);
      return;
    }

    const fila =
      inserted && typeof inserted.id === 'string' && typeof inserted.created_at === 'string'
        ? inserted
        : null;

    if (fila) {
      // Empuje optimista al estado local — el canal Realtime también lo
      // emitirá pero puede tardar 1-2s. Filtramos por id en caso de carrera.
      setMensajes((prev) =>
        prev.some((m) => m.id === fila.id)
          ? prev
          : [
              ...prev,
              {
                id: fila.id,
                sender_id: profile.id,
                receiver_id: destino,
                content: body,
                created_at: fila.created_at,
                is_mine: true,
              },
            ]
      );
    } else {
      // Sin fila no hay mensaje que notificar. El push de algo que no existe
      // le avisa a la familia de un mensaje que no puede abrir.
      setSendError('No se pudo confirmar el envío. Vuelva a intentarlo.');
      setSending(false);
      return;
    }

    // Push notification fire-and-forget — si falla no rompe el chat.
    supabase.functions
      .invoke('gym-send-push', {
        body: {
          user_id: destino,
          title: profile.full_name ?? 'Entrenador',
          body: body.slice(0, 100),
          type: 'chat',
          data: { screen: 'Chat' },
        },
      })
      .catch(() => {});
    setSending(false);
  }

  const contactosFiltrados = useMemo(
    () => contactos.filter((c) => c.full_name.toLowerCase().includes(search.trim().toLowerCase())),
    [contactos, search]
  );

  const formatDay = (iso: string) =>
    new Date(iso).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });

  const abrirDirectorio = () => {
    setSearch('');
    setMostrarDirectorio(true);
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-0 overflow-hidden rounded-xl border border-bg-border">
      {/* Columna izquierda: los hilos, o el directorio detrás del botón */}
      <div
        className={`flex flex-col bg-bg-surface border-r border-bg-border ${abierto ? 'hidden lg:flex w-72 flex-shrink-0' : 'w-full lg:w-72 lg:flex-shrink-0'}`}
      >
        <div className="p-4 border-b border-bg-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-oswald text-white text-lg uppercase tracking-wide">
              {mostrarDirectorio ? 'Nueva conversación' : 'Chat'}
            </h3>
            {mostrarDirectorio ? (
              <button
                className="text-gold-primary font-inter text-xs hover:opacity-70 transition-opacity"
                onClick={() => setMostrarDirectorio(false)}
                data-testid="monitor-volver-a-hilos"
              >
                ← Conversaciones
              </button>
            ) : (
              // La lupa y el «+» abren LO MISMO: el directorio con su
              // buscador dentro. Los alumnos no salen hasta pulsar, que es la
              // decisión; dos puertas, una implementación.
              <div className="flex items-center gap-1">
                <button
                  className="text-text-secondary hover:text-white transition-colors p-1"
                  onClick={abrirDirectorio}
                  title="Buscar contacto"
                  aria-label="Buscar contacto"
                  data-testid="monitor-buscar-contacto"
                >
                  <Search size={16} />
                </button>
                <button
                  className="text-text-secondary hover:text-white transition-colors p-1"
                  onClick={abrirDirectorio}
                  title="Nueva conversación"
                  aria-label="Nueva conversación"
                  data-testid="monitor-nueva-conversacion"
                >
                  <Plus size={18} />
                </button>
              </div>
            )}
          </div>
          {mostrarDirectorio && (
            <div className="flex items-center gap-2 bg-bg-elevated border border-bg-border rounded-lg px-3 py-2">
              <Search size={14} className="text-text-secondary" />
              <input
                type="text"
                autoFocus
                className="flex-1 bg-transparent text-white font-inter text-sm outline-none placeholder:text-text-secondary"
                placeholder="Buscar contacto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Lo que se leyó a medias, dicho una sola vez y arriba. */}
        {avisoParcial && (
          <p
            className="px-4 py-2 text-xs text-error bg-error/10 border-b border-error/30 font-inter"
            data-testid="monitor-lectura-parcial"
          >
            {avisoParcial}
          </p>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <div className="text-gold-primary font-oswald animate-pulse text-sm">Cargando...</div>
            </div>
          ) : errorCarga ? (
            <div className="p-4 text-sm space-y-2" data-testid="monitor-hilos-ilegibles">
              <p className="text-error font-inter">No se han podido cargar las conversaciones.</p>
              <p className="text-text-secondary font-inter text-xs">
                {errorCarga}. No es que no tengas ninguna.
              </p>
              <button
                className="btn-ghost text-xs"
                onClick={cargar}
                data-testid="monitor-hilos-reintentar"
              >
                Reintentar
              </button>
            </div>
          ) : mostrarDirectorio ? (
            contactosFiltrados.length === 0 ? (
              <p className="text-text-secondary font-inter text-sm text-center py-8">
                {contactos.length === 0 ? 'Sin contactos' : 'Ningún contacto con ese nombre'}
              </p>
            ) : (
              contactosFiltrados.map((c) => (
                <button
                  key={c.id}
                  onClick={() => empezarCon(c)}
                  className="w-full flex items-center gap-3 px-4 py-3 border-b border-bg-border hover:bg-bg-elevated transition-colors text-left"
                  data-testid={`monitor-contacto-${c.id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-bg-elevated border border-bg-border flex items-center justify-center font-oswald text-text-secondary text-sm flex-shrink-0">
                    {initials(c.full_name)}
                  </div>
                  <span className="font-inter text-white text-sm flex-1 truncate">
                    {c.full_name}
                  </span>
                </button>
              ))
            )
          ) : hilos.length === 0 ? (
            <p className="text-text-secondary font-inter text-sm text-center py-8">
              Sin conversaciones. Usa + para iniciar una.
            </p>
          ) : (
            hilos.map((h) => (
              <button
                key={h.clave}
                onClick={() => abrirHilo(h)}
                className={`w-full flex items-center gap-3 px-4 py-3 border-b border-bg-border hover:bg-bg-elevated transition-colors text-left ${abierto?.clave === h.clave ? 'bg-bg-elevated' : ''}`}
                data-testid={`monitor-hilo-${h.clave}`}
              >
                <div className="w-9 h-9 rounded-full bg-bg-elevated border border-bg-border flex items-center justify-center font-oswald text-text-secondary text-sm flex-shrink-0">
                  {inicialesDeFamilia(h.miembros)}
                </div>
                <span className="flex-1 min-w-0">
                  <span className="block font-inter text-white text-sm truncate">{h.nombre}</span>
                  <span className="block font-inter text-text-secondary text-xs truncate">
                    {h.ultimo}
                  </span>
                </span>
                {h.noLeidos > 0 && (
                  <span className="bg-gold-primary text-bg-primary text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                    {h.noLeidos}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className={`flex flex-col flex-1 bg-bg-primary ${!abierto ? 'hidden lg:flex' : 'flex'}`}>
        {!abierto ? (
          <div className="flex-1 flex items-center justify-center text-text-secondary font-inter text-sm">
            Selecciona una conversación
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-bg-border bg-bg-surface">
              <button
                onClick={() => setAbierto(null)}
                className="lg:hidden text-gold-primary hover:opacity-70 transition-opacity"
              >
                ←
              </button>
              <div className="w-8 h-8 rounded-full bg-bg-elevated border border-bg-border flex items-center justify-center font-oswald text-text-secondary text-xs">
                {inicialesDeFamilia(abierto.miembros)}
              </div>
              <div className="min-w-0">
                <span className="block font-oswald text-white text-base truncate">
                  {abierto.nombre}
                </span>
                {abierto.miembros.length > 1 && (
                  <span className="flex items-center gap-1 font-inter text-text-secondary text-xs">
                    <Users size={11} />
                    {abierto.miembros
                      .map((m) => m.full_name.trim())
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
              </div>
            </div>

            {/* Messages */}
            <div
              className="flex-1 overflow-y-auto p-4 space-y-1"
              data-testid="monitor-hilo-mensajes"
            >
              {errorHilo ? (
                <div
                  className="text-center text-sm pt-8 space-y-2"
                  data-testid="monitor-hilo-ilegible"
                >
                  <p className="text-error font-inter">No se ha podido leer la conversación.</p>
                  <p className="text-text-secondary font-inter text-xs">
                    {errorHilo}. No la des por nueva: puede tener historia.
                  </p>
                  <button
                    className="btn-ghost text-xs"
                    onClick={() => abierto && loadMensajes(abierto)}
                    data-testid="monitor-hilo-reintentar"
                  >
                    Reintentar
                  </button>
                </div>
              ) : (
                mensajes.length === 0 && (
                  <p className="text-text-secondary font-inter text-sm text-center pt-8">
                    Inicia la conversación
                  </p>
                )
              )}
              {mensajes.map((m, i) => {
                const prevDay = i > 0 ? formatDay(mensajes[i - 1].created_at) : null;
                const thisDay = formatDay(m.created_at);
                const firma = firmaDeLaBurbuja(m, abierto.miembros);
                return (
                  <div key={m.id}>
                    {prevDay !== thisDay && (
                      <p className="text-text-secondary font-inter text-xs text-center my-3">
                        {thisDay}
                      </p>
                    )}
                    <div className={`flex ${m.is_mine ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2.5 border ${
                          m.is_mine
                            ? 'bg-gold-primary/20 border-gold-primary/40'
                            : 'bg-bg-surface border-bg-border'
                        }`}
                      >
                        {firma && (
                          <p className="font-inter text-xs text-gold-primary/80 mb-0.5">
                            {m.is_mine ? `Para ${firma}` : firma}
                          </p>
                        )}
                        <p className="font-inter text-white text-sm leading-relaxed">{m.content}</p>
                        <p
                          className={`font-inter text-xs mt-1 text-right ${m.is_mine ? 'text-gold-primary/60' : 'text-text-secondary'}`}
                        >
                          {msgTime(m.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <div className="border-t border-bg-border bg-bg-surface">
              {sendError && (
                <div
                  className="flex items-center gap-2 px-4 pt-3 text-error text-xs font-inter"
                  data-testid="monitor-chat-send-error"
                >
                  <AlertCircle size={14} className="flex-shrink-0" />
                  <span>{sendError}</span>
                </div>
              )}
              {/* Y si el botón está apagado por algo que no se ve, lo dice sin
                  esperar a que nadie lo pulse. */}
              {!sendError && !destinatarioId && (
                <p
                  className="px-4 pt-3 text-xs text-text-secondary font-inter"
                  data-testid="monitor-envio-bloqueado"
                >
                  {MOTIVO_SIN_DESTINATARIO}
                </p>
              )}
              <div className="flex items-end gap-3 p-4">
                {/* El hilo es de la casa, pero el mensaje va a una persona:
                    `messages` sigue siendo persona a persona y cada hijo solo
                    puede leer lo suyo. Con un padre y dos hermanos, a cuál se
                    contesta no puede quedar implícito. */}
                {abierto.miembros.length > 1 && (
                  <label className="flex items-center gap-1.5 text-xs text-text-secondary font-inter flex-shrink-0 pb-2.5">
                    Para
                    <select
                      aria-label="Destinatario de la respuesta"
                      className="bg-bg-elevated border border-bg-border rounded-lg text-white text-sm px-2 py-1.5 outline-none"
                      value={destinatarioId ?? ''}
                      onChange={(e) => {
                        elegidoAManoRef.current = true;
                        setDestinatarioId(e.target.value);
                      }}
                    >
                      {abierto.miembros.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.full_name.trim() || SIN_NOMBRE}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <textarea
                  className="flex-1 bg-bg-elevated border border-bg-border rounded-xl text-white font-inter text-sm px-4 py-2.5 outline-none placeholder:text-text-secondary resize-none max-h-28 min-h-[40px] focus:border-gold-primary/50 transition-colors"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Escribe un mensaje..."
                  rows={1}
                  data-testid="monitor-chat-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <button
                  onClick={sendMessage}
                  disabled={motivoEnvioDeshabilitado !== null}
                  title={motivoEnvioDeshabilitado ?? 'Enviar'}
                  aria-label={motivoEnvioDeshabilitado ?? 'Enviar'}
                  className="w-10 h-10 rounded-full bg-gold-primary flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-40 flex-shrink-0"
                  data-testid="monitor-chat-send"
                >
                  {sending ? (
                    <span className="w-4 h-4 border-2 border-bg-primary/30 border-t-bg-primary rounded-full animate-spin" />
                  ) : (
                    <Send size={16} className="text-bg-primary" />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
